import {
  ExportedIcon,
  ReviewedIcon,
  ToReviewIcon,
} from "@/components/shared/status/invoice";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { Tags, tagNameToColor } from "@/lib/miscellaneous/tags";
import { cn } from "@/lib/utils";
import { useTenant } from "@/providers/session";
import type { DuplicateInvoice } from "@/queries/invoices/get";
import { Link, useLocation } from "@tanstack/react-router";
import { ExternalLink, Files } from "lucide-react";
import moment from "moment";
import { useTranslation } from "react-i18next";

type Props = {
  invoices: DuplicateInvoice[];
};

export const DuplicatesAlert = ({ invoices }: Props) => {
  const { t, i18n } = useTranslation();
  const { companySlug } = useTenant();
  const location = useLocation();

  const alertColor = tagNameToColor(Tags.Duplikat);

  const isClientView = location.pathname.includes("/unternehmen/");

  if (!invoices.length) return null;

  return (
    <Alert
      className={cn("mb-3 p-4")}
      variant="warning"
      style={{
        backgroundColor: alertColor.backgroundColor,
        color: "black",
        borderColor: alertColor.textColor,
        borderWidth: "1px",
        borderStyle: "solid",
      }}
    >
      <AlertTitle className="font-bold flex items-center text-base">
        <Files
          size={20}
          className="mr-2"
          style={{ color: alertColor.textColor }}
        />
        {t("possible_duplicate")}
      </AlertTitle>
      <div className="text-sm text-gray-700 mb-2">
        {t("possible_duplicate_subtitle")}
      </div>
      <div className="space-y-0">
        {invoices.map((invoice, index) => (
          <div key={invoice?.invoiceId}>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <div className="text-sm">
                  <div className="text-gray-700">
                    <span className="text-gray-500">Beleg</span>{" "}
                    <span className="font-mono font-bold">
                      {invoice?.invoiceNo
                        ? `#${invoice.invoiceNo}`
                        : t("unknown")}
                    </span>{" "}
                    <span className="text-gray-500">von</span>{" "}
                    <span className="font-semibold">
                      {invoice?.supplierName &&
                      invoice.supplierName.trim().length > 0
                        ? invoice.supplierName
                        : t("unknown")}
                    </span>{" "}
                    <span className="text-gray-500">über</span>{" "}
                    <span className="font-bold">
                      {invoice?.totalAmount != null && invoice?.currency
                        ? new Intl.NumberFormat(i18n.language, {
                            style: "currency",
                            currency: invoice.currency,
                          }).format(invoice.totalAmount)
                        : t("unknown")}
                    </span>{" "}
                    <span className="text-gray-500">vom</span>{" "}
                    <span className="font-semibold">
                      {invoice?.invoiceDate
                        ? moment(invoice.invoiceDate).format("DD.MM.YY")
                        : t("unknown")}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-2">
                    <span>Erstellt am</span>{" "}
                    <span className="font-mono">
                      {moment(invoice?.createdAt).format("DD.MM.YY HH:mm")}
                    </span>
                    {!isClientView && (
                      <InvoiceStatusForAccountant status={invoice.status} />
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 items-center ml-2">
                <Link
                  to={`/$company/${isClientView ? "unternehmen" : "buchhaltung"}/rechnung/$id`}
                  params={{ company: companySlug, id: invoice?.invoiceId }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-foreground flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-200"
                >
                  <ExternalLink className="h-4 w-4" />
                  Öffnen
                </Link>
              </div>
            </div>
            {index < invoices.length - 1 && (
              <div
                className="border-t my-0"
                style={{ borderColor: alertColor.textColor }}
              />
            )}
          </div>
        ))}
      </div>
    </Alert>
  );
};

type InvoiceStatusForAccountantProps = {
  status: string;
};

const InvoiceStatusForAccountant = ({
  status,
}: InvoiceStatusForAccountantProps) => {
  const { t } = useTranslation();

  if (status === InvoiceStatus.EXPORTED) {
    return <ExportedIcon />;
  }
  if (status === InvoiceStatus.REVIEWED) {
    return <ReviewedIcon />;
  }
  if (status === InvoiceStatus.PREPROCESSED) {
    return <ToReviewIcon />;
  }
  return null;
};
